import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from '@mui/material';

import { CurrencyCode } from 'further-types/lp/vehicle';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import React from 'react';
import useAllocationChangeHandler from '../CapitalCallAllocations/hooks/useAllocationChangeHandler';
import useAllocateShareCapital from './hooks/useAllocateShareCapital';

const StyledAccordion = styled(Accordion)({
  width: '100%',
});

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  ...theme.typography.h6,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const AccordionBody = styled(AccordionDetails)({
  padding: 0,
  border: 'none',
});

type Props = {
  title: string;
  calledFundsLabel: string;
  currency: CurrencyCode;
  vehicleId: string;
  onChange: (
    capitalBreakdown: Array<{
      commitmentId: string;
      amountLcy: number;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => void;
  capitalBreakdown: Array<{
    commitmentId: string;
    amountLcy: number;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
};

const ShareCapitalCallAllocations: React.FC<Props> = ({
  title,
  currency,
  vehicleId,
  onChange,
  capitalBreakdown,
  calledFundsLabel,
}) => {
  const { shareCapitalAllocations, isPending } = useAllocateShareCapital({
    vehicleId,
    existingBreakdown: capitalBreakdown,
    onSuccess: (allocations) => {
      onChange(allocations);
    },
  });

  const handleAllocationChange = useAllocationChangeHandler({
    capitalBreakdown,
    onAllocationsChange: onChange,
    keepCallAmountFixed: false,
  });

  const columns = [
    {
      label: 'Investor',
      key: 'investorLabel',
    },
    {
      label: 'Total capital contribution',
      key: 'totalCommitmentLcy',
    },
    {
      label: 'Uncalled capital contribution',
      key: 'unallocatedCommitmentLcy',
    },
    {
      label: 'Close date',
      key: 'closeDate',
    },
    {
      label: calledFundsLabel,
      key: 'shareCapitalCalled',
      render: (row: { commitmentId: string }) => {
        const capital = capitalBreakdown.find(
          (c) => c.commitmentId === row.commitmentId,
        );

        return (
          <NumberInput
            value={capital?.fundsCalled?.fromCommitmentLcy ?? 0}
            onChange={(value) => {
              handleAllocationChange(
                row.commitmentId,
                'fromCommitmentLcy',
                value ?? 0,
              );
            }}
            decimals={5}
            currency={currency}
            placeholder="Enter amount"
            disabled={true}
          />
        );
      },
    },
  ];

  const rows = shareCapitalAllocations.map((allocation) => ({
    id: allocation.commitmentId,
    commitmentId: allocation.commitmentId,
    investorLabel: allocation.investorLabel,
    totalCommitmentLcy: numberToCurrencyString(allocation.totalCommitmentLcy, {
      currency,
    }),
    unallocatedCommitmentLcy: numberToCurrencyString(
      allocation.unallocatedCommitmentLcy,
      { currency },
    ),
    closeDate: allocation.closeDate.toLabel(),
  }));

  return (
    <StyledAccordion defaultExpanded>
      <AccordionHeader expandIcon={<ExpandMoreIcon />}>{title}</AccordionHeader>
      <AccordionBody>
        <Table
          showHeaderBorder
          rows={rows}
          columns={columns}
          isLoading={isPending}
        />
      </AccordionBody>
    </StyledAccordion>
  );
};

export default ShareCapitalCallAllocations;
